import { type ChangeEvent, type DragEvent, type FC, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FileUp } from "lucide-react";
import { cn } from "@/utils/tailwind";

interface UploadZoneProps {
  dragActive: boolean;
  onSelect: (e: ChangeEvent<HTMLInputElement>) => void;
  onDragEnter: (e: DragEvent<HTMLLabelElement>) => void;
  onDragLeave: (e: DragEvent<HTMLLabelElement>) => void;
  onDragOver: (e: DragEvent<HTMLLabelElement>) => void;
  onDrop: (e: DragEvent<HTMLLabelElement>) => void;
  accept: string;
  isError?: boolean;
  dataTest?: string;
}

const UploadZone: FC<UploadZoneProps> = ({
  dragActive,
  onSelect,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  accept,
  isError,
  dataTest,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <label
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={cn(
        "border-input flex h-[127px] w-full cursor-pointer flex-col items-center justify-center border transition",
        dragActive && "border-primary bg-primary/20",
        isError && "border-destructive",
      )}
      data-test={dataTest ? `${dataTest}-zone` : undefined}
    >
      <FileUp className="mb-2" strokeWidth={1.5} />
      <p className="hidden font-medium md:block">
        {t("dropOr")} <span className="text-primary underline">{t("selectFile")}</span>
      </p>
      <p className="text-primary font-medium underline md:hidden">
        {t("selectFile").charAt(0).toUpperCase() + t("selectFile").slice(1)}
      </p>

      <p className="mt-1 text-xs font-extralight">{accept.replaceAll(",", " / ")}</p>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={onSelect}
        data-test={dataTest ? `${dataTest}-input` : undefined}
      />
    </label>
  );
};

export default UploadZone;
